import { useEffect, useState } from 'react'
import { Box, Paper, Switch, Typography } from '@mui/material'
import { useThemeMode } from '../../shared/hooks'
import { notify } from '../../shared/utilities/util'
import { useSetMetricsExclusionMutation } from '../../store/api/adminApi'
import { tokens } from '../assistantConfig/styles'

/**
 * Subscriber details: leave this company (NHR's own, demo and test tenants) out of every subscription metric.
 * Reads `company.excludeFromMetrics`; the switch flips only after the PATCH succeeds and the company refetches.
 */
const MetricsExclusionCard = ({ companyId, excluded }: { companyId: string; excluded: boolean }) => {
  const { isDark } = useThemeMode()
  const t = tokens(isDark)
  const [setExclusion, { isLoading: saving }] = useSetMetricsExclusionMutation()
  // The saved value shows until the company refetch catches up; after that the prop wins again.
  const [pending, setPending] = useState<boolean | null>(null)
  useEffect(() => {
    if (pending !== null && excluded === pending) setPending(null)
  }, [excluded, pending])
  const on = pending ?? excluded

  const onToggle = async (next: boolean) => {
    try {
      await setExclusion({ companyId, excludeFromMetrics: next }).unwrap()
      setPending(next)
      notify(next ? 'Excluded from subscription metrics' : 'Counted in subscription metrics', 'success')
    } catch {
      // The interceptor already toasted the failure.
    }
  }

  return (
    <Paper
      elevation={0}
      component="section"
      aria-label="Subscription metrics"
      sx={{ border: `1px solid ${t.border}`, borderRadius: '12px', p: { xs: 2.5, sm: 3 } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: { xs: 1.5, md: 3 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary' }}>
            Exclude from metrics
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.75, maxWidth: 520, lineHeight: 1.5 }}>
            On leaves this company out of MRR, trials, churn and every other number on the Metrics page. Use it for
            NHR&apos;s own company and demo or test tenants.
          </Typography>
        </Box>
        <Box
          component="label"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            minHeight: 44,
            pl: 2,
            pr: 1.5,
            borderRadius: '10px',
            border: `1px solid ${t.inputBorder}`,
            bgcolor: 'background.paper',
            fontSize: '13px',
            fontWeight: 600,
            color: 'text.primary',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.55 : 1,
          }}
        >
          Excluded
          <Switch
            checked={on}
            disabled={saving}
            onChange={(event) => void onToggle(event.target.checked)}
            inputProps={{ role: 'switch', 'aria-label': 'Exclude from subscription metrics' }}
          />
          <Box component="span" sx={{ minWidth: 22 }}>
            {on ? 'On' : 'Off'}
          </Box>
        </Box>
      </Box>
    </Paper>
  )
}

export default MetricsExclusionCard
